import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  LinearProgress,
  Button,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Box,
  TableContainer,
  Paper,
  Chip,
  Divider,
  Alert,
  Skeleton,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Switch,
  FormControlLabel,
  ToggleButton,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Search,
  Refresh,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  Save,
  CalendarToday,
  Store,
  Check as CheckIcon,
} from '@mui/icons-material';

import { APP_STATE } from 'services/Define';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import MDBox from 'components/MDBox';
import { FetchData } from 'services/Request';
import { set } from 'firebase/database';
import dayjs from 'dayjs';
import th from 'dayjs/locale/th';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale(th);

const MONTH_NAMES_TH = [
  'มกราคม',
  'กุมภาพันธ์',
  'มีนาคม',
  'เมษายน',
  'พฤษภาคม',
  'มิถุนายน',
  'กรกฎาคม',
  'สิงหาคม',
  'กันยายน',
  'ตุลาคม',
  'พฤศจิกายน',
  'ธันวาคม',
];

const formatCurrency = (amount) => `฿${Number(amount || 0).toLocaleString('th-TH')}`;

const getDaysInMonth = (month, year) => new Date(year, month, 0).getDate();

const calculateProgress = (actual, target) => {
  return target > 0 ? Math.min(100, Math.round((actual / target) * 100)) : 0;
};

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role='tabpanel' hidden={value !== index} id={`target-tabpanel-${index}`} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

TabPanel.defaultProps = {
  children: null,
};

function StatsCard({ title, value, icon: Icon, color, subtitle }) {
  return (
    <Card
      elevation={2}
      sx={{
        height: '100%',
        transition: 'all 0.3s',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display='flex' alignItems='center' justifyContent='space-between' mb={2}>
          <Box>
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ fontSize: '0.875rem', fontWeight: 500 }}
            >
              {title}
            </Typography>
            <Typography variant='h4' sx={{ fontWeight: 'bold', color: `${color}.main`, mt: 1 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}.light`,
              borderRadius: '50%',
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* <Icon sx={{ color: `${color}.main`, fontSize: 28 }} /> */}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

StatsCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.string,
  subtitle: PropTypes.string,
};

StatsCard.defaultProps = {
  color: 'primary',
  subtitle: null,
};

function ProgressCell({ progress, diff }) {
  const isPositive = diff >= 0;
  return (
    <Box sx={{ minWidth: 200 }}>
      <Box display='flex' alignItems='center' justifyContent='space-between' mb={1}>
        <Typography variant='body2' sx={{ fontWeight: 500 }}>
          {progress}%
        </Typography>
        <Chip
          icon={isPositive ? <TrendingUp /> : <TrendingDown />}
          label={`${isPositive ? '+' : ''}${Math.round(diff).toLocaleString('th-TH')}`}
          size='small'
          color={isPositive ? 'success' : 'error'}
          variant='outlined'
        />
      </Box>
      <LinearProgress
        variant='determinate'
        value={progress}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: 'grey.200',
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            backgroundColor:
              progress >= 100 ? 'success.main' : progress >= 75 ? 'warning.main' : 'error.main',
          },
        }}
      />
    </Box>
  );
}

ProgressCell.propTypes = {
  progress: PropTypes.number.isRequired,
  diff: PropTypes.number.isRequired,
};

function Target() {
  const mod = 'target';
  const app = useSelector((state) => state[APP_STATE]);
  const settingTargetPerm = app?.perm?.setting_target || false;
  const { t } = useTranslation();

  const [search, setSearch] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDay, setSelectedDay] = useState(dayjs.tz(dayjs(), 'Asia/Bangkok').date());
  const [selectedBranch, setSelectedBranch] = useState('');
  const [branchs, setBranchs] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [settingsTargets, setSettingsTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);
  const daysInMonth = useMemo(
    () => getDaysInMonth(selectedMonth, selectedYear),
    [selectedMonth, selectedYear]
  );
  const days = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth]);
  const formattedDate = useMemo(() => {
    const date = new Date(selectedYear, selectedMonth - 1, selectedDay);
    return date.toLocaleDateString('th-TH', {
      day: 'numeric',
      month: 'long',
    });
  }, [selectedYear, selectedMonth, selectedDay]);

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYear - 2 + i).sort((a, b) => b - a);
  }, []);

  const filteredMonthly = useMemo(
    () =>
      monthlyData.filter((row) =>
        (row.branch_name || '').toLowerCase().includes(search.toLowerCase())
      ),
    [monthlyData, search]
  );

  const filteredDaily = useMemo(
    () =>
      dailyData.filter((row) =>
        (row.branch_name || '').toLowerCase().includes(search.toLowerCase())
      ),
    [dailyData, search]
  );

  const totalTarget = useMemo(
    () => filteredMonthly.reduce((s, r) => s + (Number(r.target) || 0), 0),
    [filteredMonthly]
  );

  const totalSales = useMemo(
    () => filteredMonthly.reduce((s, r) => s + (Number(r.total_amount) || 0), 0),
    [filteredMonthly]
  );

  const avgProgress = useMemo(() => {
    if (filteredMonthly.length === 0) return 0;
    return Math.round(
      filteredMonthly.reduce((sum, r) => {
        const target = Number(r.target || 0);
        const amount = Number(r.total_amount || 0);
        return sum + (target > 0 ? (amount / target) * 100 : 0);
      }, 0) / filteredMonthly.length
    );
  }, [filteredMonthly]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const result = await FetchData(
        'GET',
        `/${mod}?year=${selectedYear}&month=${selectedMonth}&day=${selectedDay}`,
        app.token,
        {
          lang: app.lang,
        }
      );
      if (result.status === 'ok') {
        setMonthlyData(result.monthly || []);
        setDailyData(result.daily || []);
        setBranchs(result.branchs || []);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth, selectedDay, app.token, app.lang, mod]);

  const loadSettingsTargets = useCallback(async () => {
    if (!selectedBranch) {
      setSettingsTargets([]);
      return;
    }
    setLoading(true);
    try {
      const res = await FetchData(
        'GET',
        `/${mod}?scope=settings&year=${selectedYear}&branch_id=${selectedBranch}`,
        app.token
      );
      if (res.status === 'ok') {
        setSettingsTargets(res.targets || []);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedBranch, selectedYear, app.token, mod]);

  const handleSaveAll = useCallback(async () => {
    if (!selectedBranch) return;
    setSaving(true);
    const payload = {
      branch_id: Number(selectedBranch),
      year: Number(selectedYear),
      items: settingsTargets.map((r) => ({
        month: r.month,
        target_amount: Number(r.target || 0),
      })),
      user_id: app?.user?.id || null,
    };

    try {
      const res = await FetchData('POST', `/${mod}`, app.token, payload);
      if (res.status === 'ok') {
        await Promise.all([loadDashboard(), loadSettingsTargets()]);
      }
    } catch (error) {
      console.error('Error saving targets:', error);
    } finally {
      setSaving(false);
    }
  }, [
    selectedBranch,
    selectedYear,
    selectedMonth,
    settingsTargets,
    app,
    mod,
    loadDashboard,
    loadSettingsTargets,
  ]);

  useEffect(() => {
    if (!autoRefresh) return loadDashboard();
    const timer = setInterval(() => {
      loadDashboard();
    }, 1000 * 10);
    return () => clearInterval(timer);
  }, [loadDashboard, autoRefresh]);

  useEffect(() => {
    if (tabValue === 1) {
      loadSettingsTargets();
    }
  }, [tabValue, loadSettingsTargets]);
  // function getRequiredAvgAmount(target, afterDiscount, expectedBills) {
  //   const diff = target - afterDiscount;
  //   return diff > 0 ? diff / expectedBills : 0;
  // }
  const handleTabChange = useCallback((_, v) => setTabValue(v), []);
  const handleMonthTargetChange = useCallback(
    (month, value) => {
      const numValue = Number(value) || 0;
      setSettingsTargets((prev) => {
        const existingIndex = prev.findIndex((r) => r.month === month);
        if (existingIndex >= 0) {
          return prev.map((r, i) => (i === existingIndex ? { ...r, target: numValue } : r));
        }
        return [
          ...prev,
          { month, target: numValue, branch_id: selectedBranch, year: selectedYear },
        ];
      });
    },
    [selectedBranch, selectedYear]
  );

  const renderTable = useCallback(
    (rows, isDaily = false) => (
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider' }}
      >
        <Table>
          <TableHead sx={{ backgroundColor: 'grey.50' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', py: 2 }}>สาขา</TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 2 }}>
                {isDaily ? 'เป้าหมาย (เฉลี่ยต่อวัน)' : 'เป้าหมาย (รายเดือน)'}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 2 }}>ยอดขายรวมก่อนลด</TableCell>
              {/* <TableCell sx={{ fontWeight: "bold", py: 2 }}>ส่วนลดรวม</TableCell> */}
              {/* <TableCell sx={{ fontWeight: "bold", py: 2 }}>ยอดขายรวม</TableCell> */}
              <TableCell sx={{ fontWeight: 'bold', py: 2 }}>ยอดสุทธิ</TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 2 }}>จำนวนบิล</TableCell>
              <TableCell sx={{ fontWeight: 'bold', py: 2 }}>ยอดขายเฉลี่ย</TableCell>
              {/* <TableCell sx={{ fontWeight: "bold", py: 2 }}>บิลที่ต้องขาย</TableCell> */}
              {/* <TableCell sx={{ fontWeight: "bold", py: 2 }}>ยอดขายเฉลี่ยที่ต้องขาย</TableCell> */}
              <TableCell sx={{ fontWeight: 'bold', py: 2, minWidth: 200 }}>ความก้าวหน้า</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => {
              const target = Number(row.target || 0);
              const sales = Number(row.total_sales || 0);
              const net = Number(row.total_amount || 0);
              const discount = Number(row.total_discount || 0);
              const progress = calculateProgress(net, target);
              const diff = net - target;
              const afterDiscount = sales - Number(row.total_discount || 0);
              // const needBills = Math.ceil((target - sales) / (sales / row.count_sale_order));
              // const requiredAvgAmount = getRequiredAvgAmount(target, sales, needBills);
              return (
                <TableRow
                  key={row.branch_id || i}
                  hover
                  sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  <TableCell sx={{ py: 2 }}>
                    <Typography variant='body2' sx={{ fontWeight: 500 }}>
                      {row.branch_name}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 2 }}>{formatCurrency(target)}</TableCell>
                  <TableCell sx={{ py: 2 }}>{formatCurrency(sales)}</TableCell>
                  {/* <TableCell sx={{ py: 2 }}>{formatCurrency(discount)}</TableCell> */}
                  {/* <TableCell sx={{ py: 2 }}>{formatCurrency(afterDiscount)}</TableCell> */}
                  <TableCell sx={{ py: 2 }}>{formatCurrency(net)}</TableCell>
                  <TableCell sx={{ py: 2 }}>{row.count_sale_order}</TableCell>
                  <TableCell sx={{ py: 2 }}>{row.avg_amount}</TableCell>
                  {/* <TableCell sx={{ py: 2 }}>{needBills}</TableCell> */}
                  {/* <TableCell sx={{ py: 2 }}>{formatCurrency(requiredAvgAmount)}</TableCell> */}
                  <TableCell sx={{ py: 2 }}>
                    <ProgressCell progress={progress} diff={diff} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    ),
    []
  );

  const renderDashboard = useCallback(
    () => (
      <Box>
        <Box mb={4}>
          <Typography variant='body1' color='text.secondary'>
            ภาพรวมเป้าหมายและผลการขายของแต่ละสาขา ประจำปี {selectedYear}
          </Typography>
        </Box>

        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title='เป้าหมายรวม (เดือนนี้)'
              value={formatCurrency(totalTarget)}
              icon={CalendarToday}
              color='primary'
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title='ยอดขายสุทธิรวม (เดือนนี้)'
              value={formatCurrency(totalSales)}
              icon={TrendingUp}
              color='success'
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title='ความก้าวหน้าเฉลี่ย'
              value={`${avgProgress}%`}
              icon={TrendingUp}
              color='warning'
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title='จำนวนสาขา'
              value={filteredMonthly.length.toString()}
              icon={Store}
              color='info'
            />
          </Grid>
        </Grid>

        <Card elevation={1} sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3} alignItems='end'>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>เลือกปี</InputLabel>
                  <Select
                    value={selectedYear}
                    label='เลือกปี'
                    onChange={(e) => setSelectedYear(e.target.value)}
                    startAdornment={<CalendarToday sx={{ mr: 1, color: 'action.active' }} />}
                  >
                    {years.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>เลือกเดือน</InputLabel>
                  <Select
                    value={selectedMonth}
                    label='เลือกเดือน'
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    startAdornment={<CalendarToday sx={{ mr: 1, color: 'action.active' }} />}
                  >
                    {months.map((month) => (
                      <MenuItem key={month} value={month}>
                        {month}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={1}>
                <FormControl fullWidth>
                  <InputLabel>เลือกวัน</InputLabel>
                  <Select
                    value={selectedDay}
                    label='เลือกวัน'
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      setSelectedDay(value);
                    }}
                    startAdornment={<CalendarToday sx={{ mr: 1, color: 'action.active' }} />}
                  >
                    {days.map((day) => (
                      <MenuItem key={day} value={day}>
                        {day}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>ค้นหาสาขา</InputLabel>
                  <OutlinedInput
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    label='ค้นหาสาขา'
                    startAdornment={
                      <InputAdornment position='start'>
                        <Search />
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Box display='flex' gap={2}>
                  <Button
                    fullWidth
                    variant='contained'
                    startIcon={<Refresh />}
                    onClick={loadDashboard}
                    disabled={loading}
                    sx={{ height: 20, color: '#FFFFFF', backgroundColor: 'primary.main' }}
                  >
                    รีเฟรช
                  </Button>
                  <Switch
                    value={autoRefresh}
                    onChange={() => {
                      setAutoRefresh((prevAutoRefresh) => {
                        return !prevAutoRefresh;
                      });
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {loading ? (
          <Box>
            <Skeleton variant='rectangular' height={400} sx={{ mb: 3 }} />
            <Skeleton variant='rectangular' height={400} />
          </Box>
        ) : (
          <>
            <Card elevation={1} sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant='h6' sx={{ fontWeight: 'bold', mb: 2 }}>
                  เป้าหมายรายเดือน - {MONTH_NAMES_TH[selectedMonth - 1]} {selectedYear}
                </Typography>
                {filteredMonthly.length > 0 ? (
                  renderTable(filteredMonthly)
                ) : (
                  <Alert severity='info'>ไม่มีข้อมูลเป้าหมายรายเดือน</Alert>
                )}
              </CardContent>
            </Card>

            <Card elevation={1}>
              <CardContent>
                <Typography variant='h6' sx={{ fontWeight: 'bold', mb: 2 }}>
                  เป้าหมายรายวัน - วันนี้ {formattedDate} {selectedYear}
                </Typography>
                {filteredDaily.length > 0 ? (
                  renderTable(filteredDaily, true)
                ) : (
                  <Alert severity='info'>ไม่มีข้อมูลเป้าหมายรายวัน</Alert>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Box>
    ),
    [
      selectedYear,
      totalTarget,
      totalSales,
      avgProgress,
      filteredMonthly,
      years,
      search,
      loadDashboard,
      loading,
      renderTable,
      filteredDaily,
    ]
  );

  const renderSettings = useCallback(
    () => (
      <Box>
        <Box mb={4}>
          <Box display='flex' alignItems='center' mb={2}>
            <SettingsIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
              ตั้งค่าเป้าหมาย
            </Typography>
          </Box>
          <Typography variant='body1' color='text.secondary'>
            จัดการและกำหนดเป้าหมายการขายสำหรับแต่ละสาขา
          </Typography>
        </Box>

        <Card elevation={1} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant='h6' sx={{ fontWeight: 'bold', mb: 3 }}>
              การตั้งค่าทั่วไป
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>เลือกปี</InputLabel>
                  <Select
                    value={selectedYear}
                    label='เลือกปี'
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    {years.map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>เลือกสาขา</InputLabel>
                  <Select
                    value={selectedBranch || ''}
                    label='เลือกสาขา'
                    onChange={(e) => setSelectedBranch(e.target.value)}
                  >
                    <MenuItem value=''>
                      <em>เลือกสาขา</em>
                    </MenuItem>
                    {branchs.map((b) => (
                      <MenuItem key={b.id} value={b.id}>
                        {b.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card elevation={1}>
          <CardContent>
            <Typography variant='h6' sx={{ fontWeight: 'bold', mb: 3 }}>
              ตั้งค่าเป้าหมายรายเดือน
            </Typography>

            {!selectedBranch ? (
              <Alert severity='info'>กรุณาเลือกสาขาเพื่อตั้งค่าเป้าหมาย</Alert>
            ) : loading ? (
              <Grid container spacing={3}>
                {Array.from({ length: 12 }).map((_, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Grid item xs={12} md={6} key={`skeleton-${i}`}>
                    <Skeleton variant='rectangular' height={180} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  {months.map((month, idx) => {
                    const record = settingsTargets.find((r) => r.month === month);
                    const amount = Number(record?.target || 0);
                    const dailyAvg = amount
                      ? (amount / getDaysInMonth(month, selectedYear)).toFixed(2)
                      : 0;

                    return (
                      <Grid item xs={12} md={6} key={month}>
                        <Card variant='outlined'>
                          <CardContent>
                            <Typography variant='h6' sx={{ fontWeight: 'bold', mb: 2 }}>
                              {MONTH_NAMES_TH[idx]} {selectedYear}
                            </Typography>
                            <TextField
                              fullWidth
                              type='number'
                              label='เป้าหมายรายเดือน (บาท)'
                              value={amount || ''}
                              onChange={(e) => handleMonthTargetChange(month, e.target.value)}
                              sx={{ mb: 2 }}
                              InputProps={{
                                startAdornment: <InputAdornment position='start'>฿</InputAdornment>,
                              }}
                            />
                            <Divider sx={{ my: 1 }} />
                            <Typography variant='body2' color='text.secondary'>
                              เป้าหมายรายวันเฉลี่ย: <strong>{formatCurrency(dailyAvg)}</strong>
                            </Typography>
                            <Typography variant='body2' color='text.secondary'>
                              จำนวนวันในเดือน:{' '}
                              <strong>{getDaysInMonth(month, selectedYear)} วัน</strong>
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>

                <Box display='flex' gap={2} justifyContent='flex-end'>
                  <Button
                    variant='outlined'
                    startIcon={<Refresh />}
                    onClick={loadSettingsTargets}
                    disabled={loading}
                    sx={{ color: '#333333', backgroundColor: '#FFFFFF' }}
                  >
                    รีเฟรช
                  </Button>
                  <Button
                    variant='contained'
                    startIcon={<Save />}
                    onClick={handleSaveAll}
                    disabled={saving}
                    sx={{ minWidth: 120, color: '#FFFFFF', backgroundColor: 'primary' }}
                  >
                    {saving ? 'กำลังบันทึก...' : 'บันทึกทั้งหมด'}
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    ),
    [
      selectedYear,
      years,
      selectedBranch,
      branchs,
      loading,
      months,
      settingsTargets,
      handleMonthTargetChange,
      loadSettingsTargets,
      handleSaveAll,
      saving,
    ]
  );

  return (
    <DashboardLayout>
      <Navbar route={[t('menu.production'), t('menu.target')]} />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label='target tabs'
          sx={{
            '& .MuiTab-root': {
              minHeight: 64,
              fontSize: '1rem',
              fontWeight: 500,
            },
          }}
        >
          <Tab icon={<DashboardIcon />} iconPosition='start' label='Dashboard' />
          {settingTargetPerm && (
            <Tab icon={<SettingsIcon />} iconPosition='start' label='ตั้งค่า' />
          )}
        </Tabs>
      </Box>

      <MDBox p={3}>
        <TabPanel value={tabValue} index={0}>
          {renderDashboard()}
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          {renderSettings()}
        </TabPanel>
      </MDBox>
    </DashboardLayout>
  );
}

export default Target;
